import { useAppDispatch } from "../../hooks";
import { markAsViewed } from "../../slices/log";
import FavouriteButton from "./FavouriteButton";
import { FavouriteObjectType } from "../../types/content";

export default function CardWithImageBgAndBadgeTop({
  colour,
  image,
  href = null,
  badgeText,
  cardText,
  objectId,
  objectType,
  showFavouriteButton = true,
  newTab = false,
  sizeClass = "h-[250px] sm:h-[300px] w-[150px] sm:w-[200px] shrink-0",
}: {
  colour: string;
  image: string;
  href: string | null;
  badgeText: string;
  cardText: string;
  objectId: string;
  objectType: FavouriteObjectType;
  showFavouriteButton?: boolean;
  newTab?: boolean;
  /**
   * Size and flex behaviour of the card.
   *
   * The default is the fixed footprint the home page's horizontal carousel needs
   * — a flex-nowrap scroller only works if its items refuse to shrink. In a
   * responsive grid that same fixed width is wrong: the card stays 200px however
   * wide the column gets, so the row reads as small cards adrift in empty space.
   * Grid callers pass a fluid size instead.
   */
  sizeClass?: string;
}) {
  const dispatch = useAppDispatch();

  return (
    <div
      className={`
        relative
        ${sizeClass}
        rounded-5xl
        bg-cover
        bg-center
        px-4
        border-2
        border-${colour}
        mt-4
        cursor-pointer
        content-card
      `}
      style={{
        // Image path is relative to the 'public' folder
        backgroundImage: `url("${image}")`,
      }}
      onClick={() => {
        dispatch(markAsViewed({ objectId, objectType }))
        if (href !== null) {
          if (newTab) {
            window.open(href, "_blank");
          } else {
            window.location.href = href;
          }
        }
      }}
      data-title={cardText}
      data-tags={badgeText}
    >

      {/* Favourites button */}
      {showFavouriteButton && (
        <FavouriteButton
          objectId={objectId}
          objectType={objectType}
          dataTitle={cardText}
          dataAuthor=""
          dataTags={badgeText}
        />
      )}

      {/* Tag at top (half inside, half outside) */}
      <div
        className={`
          absolute
          top-0
          left-1/2
          -translate-x-1/2
          -translate-y-1/2
          bg-${colour}
          text-black
          font-semibold
          px-4
          py-1
          rounded-full
          shadow-md
          z-10
        `}
      >
        {badgeText}
      </div>

      {/* Text anchored to the bottom */}
      <div
        className="absolute bottom-4 left-4 text-white text-left"
      >
        <h2 className="text-sm sm:text-base md:text-lg font-bold">{cardText}</h2>
      </div>
    </div>
  );
}
